import React from 'react';
import { ProcessingStep } from '@/types';
import {
  Sparkles,
  CheckCircle2,
  Loader2,
  AlertCircle,
  XCircle,
  Cpu,
  Layers,
  ArrowRight
} from 'lucide-react';

interface ProcessingStudioScreenProps {
  steps: ProcessingStep[];
  currentStepIndex: number;
  overallPercentage: number;
  currentMessage: string;
  onCancel: () => void;
  onProceedToReview: () => void;
  isComplete: boolean;
  videoTitle: string;
}

export const ProcessingStudioScreen: React.FC<ProcessingStudioScreenProps> = ({
  steps,
  currentStepIndex,
  overallPercentage,
  currentMessage,
  onCancel,
  onProceedToReview,
  isComplete,
  videoTitle
}) => {
  return (
    <div className="flex-1 overflow-y-auto p-6 md:p-12 flex justify-center items-center ambient-gradient select-none">
      <div className="max-w-2xl w-full bg-brand-surface border border-brand-border rounded-3xl p-8 md:p-10 shadow-elevated space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-brand-accent to-brand-secondary flex items-center justify-center mx-auto shadow-glow">
            {isComplete ? (
              <CheckCircle2 className="w-8 h-8 text-brand-bg fill-brand-bg" />
            ) : (
              <Cpu className="w-8 h-8 text-brand-bg animate-pulse-subtle" />
            )}
          </div>

          <h2 className="text-xl md:text-2xl font-bold text-brand-text">
            {isComplete ? 'Participant Separation Complete!' : 'Processing Meeting Video Locally'}
          </h2>

          <p className="text-xs text-brand-muted max-w-md mx-auto">
            {isComplete
              ? `All visible participant tiles from "${videoTitle}" are ready for verification.`
              : 'Local computer-vision pipeline is clustering layout grids and extracting candidate participants.'}
          </p>
        </div>

        {/* Progress Bar & Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs font-semibold">
            <span className="text-brand-text truncate max-w-xs">{currentMessage}</span>
            <span className="font-mono text-brand-secondary text-sm tabular-nums">
              {overallPercentage}%
            </span>
          </div>

          <div className="w-full h-3 bg-brand-elevated rounded-full overflow-hidden p-0.5 border border-brand-border">
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-accent to-brand-secondary transition-all duration-300 ease-out shadow-glow"
              style={{ width: `${Math.min(100, Math.max(3, overallPercentage))}%` }}
            />
          </div>
        </div>

        {/* Step Items List */}
        <div className="space-y-2.5 pt-2">
          {steps.map((s, idx) => {
            const isDone = s.status === 'completed';
            const isRunning = s.status === 'running';

            return (
              <div
                key={s.id}
                className={`p-3.5 rounded-2xl border transition-all flex items-center justify-between ${
                  isRunning
                    ? 'bg-brand-elevated border-brand-accent/60 shadow-sm'
                    : isDone
                    ? 'bg-brand-surface/70 border-emerald-500/30'
                    : 'bg-brand-surface/40 border-brand-border/40 opacity-60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="shrink-0">
                    {isDone ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    ) : isRunning ? (
                      <Loader2 className="w-4 h-4 text-brand-accent animate-spin" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-brand-border" />
                    )}
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-brand-text">{s.title}</h4>
                    <p className="text-[10px] text-brand-muted mt-0.5">{s.description}</p>
                  </div>
                </div>

                <span className="text-[10px] font-mono uppercase tracking-wider text-brand-muted">
                  {isDone ? 'Done' : isRunning ? 'Processing' : 'Pending'}
                </span>
              </div>
            );
          })}
        </div>

        {/* Action Controls */}
        <div className="pt-4 flex items-center justify-between border-t border-brand-border/60">
          {!isComplete ? (
            <>
              <button
                onClick={onCancel}
                className="flex items-center gap-1.5 text-xs text-brand-muted hover:text-red-400 px-3 py-2 transition-colors"
              >
                <XCircle className="w-3.5 h-3.5" />
                <span>Cancel Processing</span>
              </button>
              <p className="text-[10px] text-brand-muted">
                Raw MP4 recording is securely saved.
              </p>
            </>
          ) : (
            <div className="w-full flex justify-end">
              <button
                onClick={onProceedToReview}
                className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-brand-accent to-brand-secondary text-white text-xs font-bold shadow-glow hover:shadow-[0_0_30px_rgba(124,92,252,0.5)] transition-all active:scale-95"
              >
                <span>View Separate Ready Videos</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
